// This file declares modules and entrypoints.
//
// Entrypoints are modules that expose a default export that is an async function.
// An entrypoint module registered with, for example `entrypointModule("test/abc", ...)` can be fired on the ruby side with this code:
// <%= load_page_js("test/abc", 10000, *params) %> [where params is a list of raw strings]
// This will cause the default async function to be run using the provided params.
//
// entrypointSvelte is a shortcut that takes as the first parameter a query-selector that is used to mount the
// svelte component on. This skips the requirement of a default async function in this case.
//
// exportModule is to define modules for use within the old module loader (interop with the legacy module code).
import { entrypointHTML, entrypointSvelte, entrypointModule, exportModule, exportModuleRaw } from '@/utils/pages';

exportModuleRaw("widgets/helpers/translations", () => import(/* webpackPrefetch: true, webpackPreload: true */ "@/utils/translations"));

// Export some modules to the old LocalModuleLoader
exportModule("tinymce", () => import("@/utils/tinymce"));
exportModule("underscore", () => import("underscore"));
exportModule("widgets/bootstrap/modal", () => import("@/backend/layout/modal"));
exportModuleRaw("scripts", () => import("@/utils/scripts"));

exportModuleRaw("widgets/message_box", () => import("@/utils/dialogs"));


// This is for layout specific things.
entrypointSvelte("layout/organizer-chooser/header", () => import(/* webpackPrefetch: true, webpackPreload: true */ "@/backend/layout/organizer-chooser/Header.svelte"));
entrypointSvelte("layout/organizer-chooser/none-selected", () => import("@/backend/layout/organizer-chooser/NoneSelected.svelte"));
entrypointModule("layout/help-pages/edit", () => import("@/backend/layout/help-pages/edit"));
entrypointModule("layout/sidenav", () => import(/* webpackPrefetch: true, webpackPreload: true */ "@/backend/layout/sidenav"));
entrypointModule("layout/changelog/view", () => import("@/backend/layout/changelog/index"));
exportModuleRaw("layout/changelog/admin", () => import("@/backend/layout/changelog/admin"));
entrypointHTML("layout/spa/not_found", new URL("./backend/layout/spa-404.html", import.meta.url));

// Bookings
entrypointModule("bookings/user_fees", () => import("@/backend/bookings/user_fees/index"));
// Standard "Buchungen" list (declarative datatable).
entrypointModule("page/bookings/list/index", () => import("@/backend/bookings/list/index"));
// Helpdesk booking list (declarative datatable, cards drawn by its rowHandler).
entrypointModule("page/helpdesk/index", () => import("@/backend/helpdesk/index"));
entrypointModule("bookings/modal", () => import("@/backend/bookings/modal"));
entrypointModule("bookings/retry_payment", () => import("@/backend/bookings/payments"));
entrypointModule("bookings/tickets", () => import("@/backend/bookings/tickets/modal"));
entrypointModule("bookings/tickets/embedded", () => import("@/backend/bookings/tickets/embedded"));
entrypointModule("page/bookings/open_amount/index", () => import("@/backend/bookings/open_amount/index"));

entrypointModule("booking/receipt/user", () => import("@/backend/bookings/receipt/user"));

entrypointModule("tickets/index", () => import("@/backend/tickets/index"));

// Older modules moved to webpack.
entrypointModule("page/register/step/4", () => import("@/backend/registration/step4"));

// New modules
entrypointModule("page/users/registered/index", () => import("@/backend/users/registered_users/index"));
entrypointModule("page/users/backend/index", () => import("@/backend/users/backend_users/index"));
entrypointModule("page/users/buyer/index", () => import("@/backend/users/buyer/index"));
entrypointModule("page/users/permitted", () => import("@/backend/users/permitted"));
entrypointModule("page/users/profile/uac/mfa", () => import("@/backend/users/uac/mfa"));
entrypointModule("page/users/profile/uac/webauthn", () => import("@/backend/users/uac/webauthn"));

entrypointModule("page/text_template/index", () => import("@/backend/text_template/index"));
entrypointModule("page/text_template/global", () => import("@/backend/text_template/global"));

entrypointModule("page/template_management/index", () => import("@/backend/template_management/index"));
entrypointModule("page/template_management/view", () => import("@/backend/template_management/view"));

entrypointModule("page/event_management/index", () => import("@/backend/event_management/index"));
exportModuleRaw("page/event_management/mass_mail", () => import("@/backend/event_management/mail"));
entrypointModule("page/event_management/edit_page", () => import("@/backend/event_management/edit_page"));
entrypointModule("page/event_management/location", () => import("@/backend/event_management/locations/index"));
entrypointModule("page/event_management/mass_mail/page", () => import("@/backend/event_management/mail/page"));
entrypointModule("page/event_management/seating_chart", () => import("@/seating_chart_editor/boot"));
entrypointModule("page/seating_chart/list", () => import("@/backend/seating_charts/list"));

entrypointModule("page/forms/edit", () => import("@/backend/forms/editor"));

entrypointModule("page/point_of_sale/cms_page_edit", () => import("@/backend/points_of_sale/cms"));
entrypointModule("page/point_of_sale/cms_page_index", () => import("@/backend/points_of_sale/cms_page_index"));
entrypointModule("page/point_of_sale/main", () => import("@/backend/points_of_sale/index"));
entrypointModule("page/point_of_sale/edit", () => import("@/backend/points_of_sale/edit"));
entrypointModule("page/point_of_sale/translations", () => import("@/backend/points_of_sale/translations"));
entrypointModule("page/point_of_sale/coupons", () => import("@/backend/points_of_sale/coupons/index"));
entrypointModule("page/point_of_sale/methods", () => import("@/backend/points_of_sale/methods"));
entrypointModule("page/point_of_sale/box_office/permissions", () => import("@/backend/points_of_sale/box_office/permissions"));
entrypointModule("page/point_of_sale/box_office/rebates", () => import("@/backend/points_of_sale/box_office/rebates"));
entrypointModule("page/point_of_sale/embed/info", () => import("@/backend/points_of_sale/embeds/info"));
entrypointModule("page/point_of_sale/embed/wizards", () => import("@/backend/points_of_sale/embeds/wizards"));
entrypointModule("page/point_of_sale/tracking", () => import("@/backend/points_of_sale/tracking"));
entrypointModule("page/point_of_sale/designs", () => import("@/backend/points_of_sale/designs"));
entrypointModule("page/point_of_sale/list_designs", () => import("@/backend/points_of_sale/list_designs"));

entrypointModule("page/organizer_management/index", () => import("@/backend/organizer_management/index"));

entrypointModule("page/discounts/rebate", () => import("@/backend/discounts/rebate/index"));
entrypointModule("page/discounts/bundle/main", () => import("@/backend/discounts/bundle/main"));

entrypointModule("page/box_office/index", () => import("@/backend/box_office"));

entrypointModule("page/billing/index", () => import("@/backend/billing/index"));

entrypointModule("page/dashboard/index", () => import("@/backend/dashboard/index"));

entrypointModule("page/coupons/assignments/main", () => import("@/backend/coupons/assignments"));
entrypointModule("page/coupons/drafts/main", () => import("@/backend/coupons/drafts"));
entrypointModule("page/coupons/owners", () => import("@/backend/coupons/owners"));

entrypointModule("page/evaluations/table", () => import("@/backend/evaluations/table"));

entrypointModule("page/settings/generator/main", () => import("@/backend/settings/generator"));
entrypointModule("page/settings/locales/main", () => import("@/backend/settings/locales"));
entrypointModule("page/settings/payments/main", () => import("@/backend/settings/payments"));
entrypointModule("page/settings/invoice_background/main", () => import("@/backend/settings/invoice_background"));

entrypointModule("page/scan_area/main", () => import("@/backend/scan_area/main"));
entrypointModule("page/scan_area/barcode", () => import("@/backend/scan_area/barcode"));

entrypointSvelte("page/live_scan/main", () => import("@/backend/live_scan/LiveScan.svelte"));

entrypointModule("evaluation_v3", () => import("@/backend/evaluations/v3/page"));
entrypointModule("evaluations/matrix", () => import("@/backend/evaluations/matrix"));

entrypointHTML("page/user_evaluations", new URL("./backend/user_evaluations/page.html", import.meta.url));
entrypointSvelte("page/user_evaluations/list", () => import("@/backend/user_evaluations/List.svelte"));
entrypointSvelte("page/user_evaluations/edit", () => import("@/backend/user_evaluations/Edit.svelte"));
entrypointSvelte("page/user_evaluations/new", () => import("@/backend/user_evaluations/Create.svelte"));
